import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { RefreshCw, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDate } from "@/lib/date";

const EVENT_META = {
  "Diagnosis": { emoji: "🩺", color: "text-purple-700 bg-purple-100" },
  "Medication Updated": { emoji: "💊", color: "text-green-700 bg-green-100" },
  "Laboratory Results": { emoji: "🧪", color: "text-orange-700 bg-orange-100" },
  "Consultation": { emoji: "👨‍⚕️", color: "text-teal-700 bg-teal-100" },
  "Follow-up Scheduled": { emoji: "📅", color: "text-blue-700 bg-blue-100" },
  "Clinical Alert": { emoji: "⚠️", color: "text-red-700 bg-red-100" },
  "Procedure": { emoji: "💉", color: "text-indigo-700 bg-indigo-100" },
  "Hospital Admission": { emoji: "🏥", color: "text-rose-700 bg-rose-100" },
};

const FILTERS = [
  ["all", "All"],
  ["diagnosis", "Diagnosis"],
  ["medicines", "Medicines"],
  ["lab_results", "Lab Results"],
  ["consultations", "Consultations"],
  ["followups", "Follow-ups"],
  ["procedures", "Procedures"],
  ["admissions", "Admissions"],
  ["alerts", "Alerts"],
];

const EMPTY_FORM = { type: "Procedure", title: "", date: "", notes: "" };

export default function PatientTimelineTab({ patientId }) {
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ai/patients/${patientId}/timeline`);
      setTimeline(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const saveProcedure = async () => {
    if (!form.title.trim() || !form.date) {
      toast.error("Title and date are required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/procedures`, form);
      toast.success(`${form.type} logged`);
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save record");
    } finally {
      setSaving(false);
    }
  };

  const events = timeline?.events || [];
  const filteredEvents = filter === "all" ? events : events.filter((e) => e.category === filter);

  return (
    <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="patient-timeline-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-slate-900">AI Patient Timeline</h2>
          {timeline && <p className="mt-1 text-xs text-slate-500" data-testid="timeline-last-generated">Last Generated: {formatDate(timeline.generated_at)}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="add-procedure-btn" size="sm" variant="outline" onClick={() => setShowForm((s) => !s)} className="gap-2">
            <Plus className="h-3 w-3" /> Log Procedure / Admission
          </Button>
          <button data-testid="refresh-timeline-btn" onClick={load} disabled={loading} className="flex items-center gap-2 rounded-full border border-teal-700 px-3 py-1.5 text-xs font-medium text-teal-700 hover:bg-teal-50 disabled:opacity-50">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {showForm && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="procedure-form">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="procedure-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Procedure">Procedure</SelectItem>
                  <SelectItem value="Hospital Admission">Hospital Admission</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <DatePicker testId="procedure-date-input" value={form.date} onChange={(v) => setForm({ ...form, date: v })} toYear={new Date().getFullYear()} />
            </div>
            <div className="sm:col-span-2">
              <Label>Title</Label>
              <Input data-testid="procedure-title-input" placeholder="e.g. Retinal Laser Photocoagulation" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Notes (optional)</Label>
              <Textarea data-testid="procedure-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button data-testid="save-procedure-btn" size="sm" disabled={saving} onClick={saveProcedure} className="bg-teal-700 hover:bg-teal-800">
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2" data-testid="timeline-filters">
        {FILTERS.map(([key, label]) => (
          <button
            key={key}
            data-testid={`timeline-filter-${key}`}
            onClick={() => setFilter(key)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              filter === key ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && <p className="mt-6 text-sm text-slate-400">Loading timeline...</p>}
      {!loading && filteredEvents.length === 0 && (
        <p className="mt-6 text-sm text-slate-400" data-testid="timeline-empty-state">
          {events.length === 0
            ? "No recorded history yet — diagnosis, consultations, medicines, lab results and follow-ups will appear here chronologically once added."
            : "No events match this filter yet."}
        </p>
      )}

      {filteredEvents.length > 0 && (
        <div className="mt-6" data-testid="timeline-results">
          {filteredEvents.map((e, i) => {
            const meta = EVENT_META[e.event_type] || { emoji: "•", color: "text-slate-600 bg-slate-100" };
            return (
              <div key={`${e.date}-${e.event_type}-${i}`}>
                <div className="flex gap-3 py-3" data-testid={`timeline-event-${i}`}>
                  <div className={`flex h-9 w-9 flex-none items-center justify-center rounded-full text-base ${meta.color}`}>
                    <span>{meta.emoji}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-slate-400">{formatDate(e.date)}</p>
                    <p className="text-sm font-semibold text-slate-900">{e.event_type}</p>
                    {e.bullets?.length > 0 ? (
                      <ul className="mt-1 list-inside list-disc space-y-0.5 text-sm text-slate-700">
                        {e.bullets.map((b, bi) => <li key={`${bi}-${b}`}>{b}</li>)}
                      </ul>
                    ) : (
                      <p className="mt-1 text-sm text-slate-400">No additional details recorded.</p>
                    )}
                  </div>
                </div>
                {i < filteredEvents.length - 1 && <hr className="border-slate-100" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
